import clsx from 'clsx';
import { ComponentProps, CSSProperties, PropsWithChildren, ReactElement, useContext } from 'react';
import { Placement } from 'react-aria';
import {
  Collection,
  ComboBoxStateContext,
  Header,
  ListBox,
  ListBoxItem,
  ListBoxLoadMoreItem,
  ListBoxProps,
  ListBoxSection,
  OverlayTriggerStateContext,
  Popover,
  PopoverProps,
  SelectStateContext,
  Text,
} from 'react-aria-components';
import CircleSpinner from '~/components/data-display/CircleSpinner/CircleSpinner';
import SimpleTooltip from '~/components/data-display/SimpleTooltip';
import useDataTestId from '~/hooks/useDataTestId';
import { useEllipsisTooltip } from '~/hooks/useEllipsisTooltip';
import CheckIcon from '~/icons/icon-check.svg?react';
import styles from './ListBoxPopover.module.scss';

export interface ListBoxOptionEntry {
  action?: ReactElement;
  endIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  id: string;
  label: string;
  Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  media?: ReactElement;
  description?: string;
  textValue: string;
  isDisabled?: boolean;
  className?: string;
  wrapLabel?: boolean;
  children?: never;
}
interface ListBoxOptionHeader {
  id: string;
  label: string;
  children: ListBoxOptionEntry[];
}

export type ListBoxOption = ListBoxOptionEntry | ListBoxOptionHeader;

const isListBoxOptionHeader = (option: ListBoxOption): option is ListBoxOptionHeader => {
  return 'children' in option;
};

interface ContainerProps extends Pick<
  PopoverProps,
  'triggerRef' | 'isOpen' | 'onOpenChange' | 'isNonModal' | 'offset'
> {
  additionalContent?: ReactElement;
  additionalContentSticky?: boolean;
  headerContent?: ReactElement;
  maxHeight?: number;
  shouldFlip?: boolean;
  dataTestId?: string;
  placement?: Placement;
  style?: CSSProperties;
  ref?: ComponentProps<typeof Popover>['ref'];
}

interface Props extends ContainerProps {
  additionalContent?: ReactElement;
  additionalContentSticky?: boolean;
  headerContent?: ReactElement;
  emptyState?: ReactElement;
  options: ListBoxOption[];
  onAction?: ComponentProps<typeof ListBoxItems>['onAction'];
  ariaLabel?: string;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
}

const stopEvent = (e: React.SyntheticEvent) => {
  e.stopPropagation();
  e.nativeEvent.stopImmediatePropagation();
};

const additionalContentInteractiveSelector = 'button, a, input, select, textarea, [role="button"], [role="link"]';

const isInteractiveTarget = (e: React.SyntheticEvent) =>
  e.target instanceof Element && !!e.target.closest(additionalContentInteractiveSelector);

const useClosePopover = () => {
  const comboBoxContext = useContext(ComboBoxStateContext);
  const overlayContext = useContext(OverlayTriggerStateContext);
  const selectContext = useContext(SelectStateContext);

  return () => (selectContext ?? comboBoxContext ?? overlayContext)?.close();
};

const ListBoxItemAction: React.FC<{ children: ReactElement }> = ({ children }) => {
  const close = useClosePopover();
  const stopIfInteractive = (e: React.SyntheticEvent) => {
    if (isInteractiveTarget(e)) stopEvent(e);
  };
  return (
    <div
      onPointerDown={stopIfInteractive}
      onPointerUp={(e) => {
        if (!isInteractiveTarget(e)) return;
        stopEvent(e);
        close();
      }}
      onClick={stopIfInteractive}
    >
      {children}
    </div>
  );
};

const ListBoxAdditionalContent: React.FC<{ children?: ReactElement }> = ({ children }) => {
  const close = useClosePopover();
  const closeIfInteractiveTarget = (event: React.SyntheticEvent) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest(additionalContentInteractiveSelector)) {
      queueMicrotask(close);
    }
  };
  const closeIfActivatedByKeyboard = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    closeIfInteractiveTarget(event);
  };

  if (!children) {
    return null;
  }

  return (
    <div onPointerDownCapture={closeIfInteractiveTarget} onKeyDownCapture={closeIfActivatedByKeyboard}>
      {children}
    </div>
  );
};

export const ListBoxOptionItem: React.FC<{ option: ListBoxOptionEntry }> = ({ option }) => {
  const {
    label,
    id,
    textValue,
    className,
    isDisabled,
    Icon,
    media,
    description,
    action,
    endIcon: EndIcon,
    wrapLabel,
  } = option;
  const { ref, title } = useEllipsisTooltip(label);

  return (
    <ListBoxItem
      id={id}
      textValue={textValue}
      className={clsx(styles.item, { [styles.hasAction!]: !!action }, className)}
      aria-label={label}
      isDisabled={isDisabled}
    >
      {(Icon || media) && (
        <div className={styles.leadingIcon}>
          {Icon && <Icon className={styles.icon} />}
          {media && <div className={styles.media}>{media}</div>}
        </div>
      )}
      <div className={styles.itemText}>
        <div className={styles.labelContainer}>
          <SimpleTooltip title={title} mode="span" className={clsx(styles.label, wrapLabel && styles.wrapLabel)}>
            <Text ref={ref} slot="label" className={clsx(styles.label, wrapLabel && styles.wrapLabel)}>
              {label}
            </Text>
          </SimpleTooltip>
        </div>
        {description && <Text slot="description">{description}</Text>}
      </div>
      {action ? (
        <div className={styles.actionWithCheck}>
          <ListBoxItemAction>{action}</ListBoxItemAction>
          <CheckIcon className={styles.check} />
        </div>
      ) : (
        <CheckIcon className={styles.check} />
      )}
      {EndIcon && <EndIcon className={styles.endIcon} />}
    </ListBoxItem>
  );
};

export const ListBoxPopoverContainer: React.FC<PropsWithChildren<ContainerProps>> = (props) => {
  const dt = useDataTestId(props.dataTestId);
  return (
    <Popover
      ref={props.ref}
      className={styles.popover}
      maxHeight={props.maxHeight}
      shouldFlip={props.shouldFlip}
      offset={props.offset}
      data-testid={dt()}
      placement={props.placement}
      triggerRef={props.triggerRef}
      isOpen={props.isOpen}
      onOpenChange={props.onOpenChange}
      isNonModal={props.isNonModal}
      style={props.style}
    >
      {props.headerContent && <div className={styles.stickyHeader}>{props.headerContent}</div>}
      {props.children}
      {props.additionalContentSticky ? (
        <div className={styles.stickyFooter}>
          <ListBoxAdditionalContent>{props.additionalContent}</ListBoxAdditionalContent>
        </div>
      ) : (
        <ListBoxAdditionalContent>{props.additionalContent}</ListBoxAdditionalContent>
      )}
    </Popover>
  );
};

export const ListBoxItems: React.FC<
  ListBoxProps<ListBoxOption> & { options: ListBoxOption[]; onLoadMore?: () => void; isLoadingMore?: boolean }
> = ({ options, onLoadMore, isLoadingMore, ...props }) => {
  const renderSection = (section: ListBoxOptionHeader) => (
    <ListBoxSection id={section.id}>
      <Header className={styles.header}>{section.label}</Header>
      <Collection items={section.children}>{renderOption}</Collection>
    </ListBoxSection>
  );

  const renderOption = (option: ListBoxOptionEntry) => <ListBoxOptionItem key={option.id} option={option} />;

  return (
    <ListBox {...props} className={styles.listBox}>
      <Collection items={options}>
        {(item) => (isListBoxOptionHeader(item) ? renderSection(item) : renderOption(item))}
      </Collection>
      {onLoadMore && (
        <ListBoxLoadMoreItem className={styles.loadMoreItem} onLoadMore={onLoadMore} isLoading={isLoadingMore}>
          <CircleSpinner />
        </ListBoxLoadMoreItem>
      )}
    </ListBox>
  );
};

const ListBoxPopover: React.FC<Props> = ({ onAction, ariaLabel, ...props }: Props) => {
  return (
    <ListBoxPopoverContainer {...props}>
      <ListBoxItems
        options={props.options}
        onAction={onAction}
        aria-label={ariaLabel}
        onLoadMore={props.onLoadMore}
        isLoadingMore={props.isLoadingMore}
        renderEmptyState={() => props.emptyState}
      />
    </ListBoxPopoverContainer>
  );
};

export default ListBoxPopover;
